/**
 * Iconos en línea. Nada de fuentes de iconos ni CDN: el portal vive en red
 * interna y no debe depender de nada de fuera.
 */
const base = { className: 'nav__icono', viewBox: '0 0 24 24', 'aria-hidden': true } as const;

export const IconoPanel = () => (
  <svg {...base}>
    <rect x="3" y="3" width="7" height="9" rx="1" />
    <rect x="14" y="3" width="7" height="5" rx="1" />
    <rect x="14" y="12" width="7" height="9" rx="1" />
    <rect x="3" y="16" width="7" height="5" rx="1" />
  </svg>
);

export const IconoDocumentos = () => (
  <svg {...base}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
  </svg>
);

export const IconoCategorias = () => (
  <svg {...base}>
    <path d="M4 6h6l2 2h8v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1z" />
  </svg>
);

export const IconoGrupos = () => (
  <svg {...base}>
    <circle cx="9" cy="8" r="3" />
    <path d="M3 20a6 6 0 0 1 12 0" />
    <path d="M16 6.5a3 3 0 0 1 0 5.8M17 20a6 6 0 0 0-2-4.4" />
  </svg>
);

export const IconoUsuarios = () => (
  <svg {...base}>
    <circle cx="12" cy="8" r="3.2" />
    <path d="M5 20a7 7 0 0 1 14 0" />
  </svg>
);

export const IconoDispositivos = () => (
  <svg {...base}>
    <rect x="3" y="5" width="13" height="9" rx="1" />
    <path d="M1.5 17.5h16" />
    <rect x="17" y="9" width="5.5" height="11" rx="1" />
  </svg>
);

export const IconoBitacora = () => (
  <svg {...base}>
    <path d="M5 4h11l3 3v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" />
    <path d="M8 10h8M8 14h6" />
  </svg>
);

export const IconoSalir = () => (
  <svg {...base} style={{ width: '.9rem', height: '.9rem' }}>
    <path d="M9 21H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h4" />
    <path d="M16 17l5-5-5-5M21 12H9" />
  </svg>
);

/**
 * La palomita de una casilla marcada.
 *
 * Antes se dibujaba con el truco de siempre: una caja con dos bordes, girada
 * 45°. Centrarla se puede calcular —tras el giro el trazo cae 0,88 px por debajo
 * del centro—, pero a 17 px de lado el problema no es el centro: son los bordes.
 * Un borde de 2 px dibujado en diagonal reparte el antialiasing de forma
 * desigual entre sus dos brazos, y el ojo lo lee como una palomita torcida por
 * mucho que las cuentas cuadren.
 *
 * Un trazo con extremos y vértice redondeados se dibuja igual de nítido en las
 * dos direcciones, y el `viewBox` lo centra sin números mágicos.
 */
export const Palomita = () => (
  <svg className="casilla-ficha__palomita" viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M3.5 8.5 6.5 11.5 12.5 5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Descargar: flecha hacia una línea. Es el gesto que la gente reconoce sin
 * leerlo, y por eso el botón lo lleva además del texto: en una fila con varias
 * acciones, el icono es lo que se encuentra de un vistazo.
 */
export const IconoDescargar = () => (
  <svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true">
    <path
      d="M8 2.5v7M4.8 6.8 8 10l3.2-3.2M3 12.8h10"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/*
 * Los de las acciones de las tablas: un icono por verbo, siempre el mismo para
 * el mismo verbo en todo el portal. Van con `AccionIcono`, que les pone el
 * nombre en el tooltip y para el lector de pantalla.
 */
export const IconoEditar = () => (
  <svg {...base}>
    <path d="M4 20h4L19 9l-4-4L4 16z" />
    <path d="M13.5 6.5l4 4" />
  </svg>
);

export const IconoDesactivar = () => (
  <svg {...base}>
    <path d="M12 3v8" />
    <path d="M6.3 6.8a8 8 0 1 0 11.4 0" />
  </svg>
);

export const IconoActivar = () => (
  <svg {...base}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M10 8.5l5.5 3.5-5.5 3.5z" />
  </svg>
);

export const IconoCandado = () => (
  <svg {...base}>
    <rect x="5" y="10.5" width="14" height="10" rx="1.5" />
    <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
  </svg>
);

export const IconoDesbloquear = () => (
  <svg {...base}>
    <rect x="5" y="10.5" width="14" height="10" rx="1.5" />
    <path d="M8 10.5V7.5a4 4 0 0 1 7.6-1.8" />
  </svg>
);

export const IconoEnviar = () => (
  <svg {...base}>
    <path d="M21 3 10 14" />
    <path d="M21 3l-6.5 18-4.5-7-7-4.5z" />
  </svg>
);

export const IconoLlave = () => (
  <svg {...base}>
    <circle cx="8" cy="15" r="4" />
    <path d="M11 12l9-9M17 6l2.5 2.5M15 8l2 2" />
  </svg>
);

export const IconoFoto = () => (
  <svg {...base}>
    <path d="M4 8h3l1.5-2.5h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
    <circle cx="12" cy="13" r="3.5" />
  </svg>
);

export const IconoEscudo = () => (
  <svg {...base}>
    <path d="M12 3l7 3v5c0 4.5-3 8.2-7 10-4-1.8-7-5.5-7-10V6z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

export const IconoQuitarPersona = () => (
  <svg {...base}>
    <circle cx="10" cy="8" r="3.2" />
    <path d="M3.5 20a6.5 6.5 0 0 1 11-4.7" />
    <path d="M16.5 15.5l4 4M20.5 15.5l-4 4" />
  </svg>
);

export const IconoActividad = () => (
  <svg {...base}>
    <path d="M3 12h4l2.5-6 5 12 2.5-6h4" />
  </svg>
);

export const IconoVer = () => (
  <svg {...base}>
    <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export const IconoCampana = () => (
  <svg {...base}>
    <path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15z" />
    <path d="M10 20.5a2 2 0 0 0 4 0" />
  </svg>
);

export const IconoEtiqueta = () => (
  <svg {...base}>
    <path d="M3.5 12.5V4.5a1 1 0 0 1 1-1h8l8 8-9 9z" />
    <circle cx="8.5" cy="8.5" r="1.5" />
  </svg>
);

export const IconoPublicar = () => (
  <svg {...base}>
    <path d="M12 16V5M7.5 9.5 12 5l4.5 4.5" />
    <path d="M4.5 15v4a1 1 0 0 0 1 1h13a1 1 0 0 0 1-1v-4" />
  </svg>
);

export const IconoBorrar = () => (
  <svg {...base}>
    <path d="M4.5 7h15M9.5 7V4.5h5V7" />
    <path d="M6.5 7l1 13h9l1-13M10 11v5.5M14 11v5.5" />
  </svg>
);

export const IconoAprobar = () => (
  <svg {...base}>
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);

export const IconoRechazar = () => (
  <svg {...base}>
    <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />
  </svg>
);

export const IconoRetirar = () => (
  <svg {...base}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M6 18 18 6" />
  </svg>
);

export const IconoLeer = () => (
  <svg {...base}>
    <path d="M3 5.5c3.5-1.5 6.5-1 9 1 2.5-2 5.5-2.5 9-1V19c-3.5-1.5-6.5-1-9 1-2.5-2-5.5-2.5-9-1z" />
    <path d="M12 6.5V20" />
  </svg>
);
